import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProgramCard from './components/ProgramCard';
import EnrollmentManagement from './components/EnrollmentManagement';
import UnitManagement from './components/UnitManagement';
import StatisticsPanel from './components/StatisticsPanel';
import ProgramModal from './components/ProgramModal';
import ProgramDetailsModal from './components/ProgramDetailsModal';
import RoomManagement from './components/RoomManagement';
import { programService } from '../../services/programService';
import { academicCalendarService } from '../../services/academicCalendarService';
import AdminLayout from '../../components/layouts/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';

import { useNavigate, useLocation } from 'react-router-dom';

const AcademicManagement = ({ initialTab, embedded = false, myUnitsOnly = false, onBack = null }) => {
  const Layout = embedded ? ({ children }) => <>{children}</> : AdminLayout;
  const { profile } = useAuth();
  const location = useLocation();
  const validTabs = ['programs', 'units', 'rooms', 'enrollment', 'statistics'];
  const urlTab = new URLSearchParams(location.search).get('tab');
  const requestedTab = validTabs.includes(urlTab) ? urlTab : initialTab || location.state?.tab || 'programs';
  const [activeTab, setActiveTab] = useState(['calendar', 'archive'].includes(requestedTab) ? 'programs' : requestedTab); // programs, units, rooms, enrollment, statistics
  const navigate = useNavigate();
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');

  // Modal states
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);

  const [selectedProgramForUnits, setSelectedProgramForUnits] = useState(null);
  const [selectedUnitType, setSelectedUnitType] = useState('all');

  // NEW: State for real program data from database
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Keep the UI aligned with the database permission matrix. Programme
  // creation is available to institution administrators and Heads of
  // Department who manage programmes for their department.
  const programmeCreatorRoles = new Set([
    'system_administrator',
    'chief_principal',
    'deputy_principal_academic',
    'head_of_department'
  ]);
  const currentRoles = [profile?.primary_role, ...(profile?.secondary_roles || [])].filter(Boolean);
  const canCreateProgram = currentRoles.some(role => programmeCreatorRoles.has(role));

  useEffect(() => {
    const queryTab = new URLSearchParams(location.search).get('tab');
    const fallbackTab = initialTab || location.state?.tab || 'programs';
    const nextTab = validTabs.includes(queryTab)
      ? queryTab
      : (['calendar', 'archive'].includes(fallbackTab) ? 'programs' : fallbackTab);
    setActiveTab(current => current === nextTab ? current : nextTab);
  }, [location.search, initialTab, location.state?.tab]);

  // NEW: Fetch programs from database on component mount
  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await programService?.getAll();

      // Transform database data to match UI expectations
      const transformedPrograms = data?.map(program => ({
        id: program?.id,
        name: program?.name,
        code: program?.code,
        levels: program?.levels || [], // Multi-level support
        levelDurations: program?.levelDurations || {}, // Level-specific durations
        levelCapacities: program?.levelCapacities || {}, // Level-specific capacities
        status: program?.isActive ? 'Active' : 'Inactive',
        department: program?.department?.name || 'Unknown',
        faculty: program?.department?.faculty?.name || 'Unknown', // Added faculty
        departmentId: program?.departmentId,
        // Calculate stats from database (would need additional queries in real scenario)
        enrolledStudents: program?.enrolledStudents || 0,
        capacity: program?.capacity || 0,
        totalUnits: program?.totalUnits || 0, // Using real count from service
        coreUnits: program?.coreUnits || 0,
        commonUnits: program?.commonUnits || 0,
        basicUnits: program?.basicUnits || 0,
        duration: Object.values(program?.levelDurations || {})?.[0] || 'N/A',
        createdAt: program?.created_at, // Use created_at from DB
        updatedAt: program?.updatedAt
      }));

      setPrograms(transformedPrograms);
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError(err?.message || 'Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  const [activeAcademicYear, setActiveAcademicYear] = useState(null);

  useEffect(() => {
    const fetchActiveYearAndCheckSemester = async () => {
      try {
        const years = await academicCalendarService.getAcademicYears();
        const activeYear = years.find(y => y.status === 'active');
        setActiveAcademicYear(activeYear);

        // Find active semester in active year
        if (activeYear) {
          const sems = await academicCalendarService.getSemesters(activeYear.id);
          const activeSem = sems.find(s => s.status === 'active');

          let shouldPromptDeactivation = false;
          let message = "";

          if (activeSem && activeSem.end_date) {
            const endDate = new Date(activeSem.end_date);
            const today = new Date();
            if (today > endDate) {
              console.log('⏰ Active semester has ended. Checking for student deactivation...');

              // We check if there are any active/admitted students left
              const { count: activeStudentsCount } = await supabase
                .from('students')
                .select('*', { count: 'exact', head: true })
                .in('status', ['Active', 'Admitted', 'active', 'admitted', 'ACTIVE', 'ADMITTED']);

              if (activeStudentsCount > 0) {
                if (window.confirm(`The current semester (${activeSem.name}) has ended. Would you like to deactivate ${activeStudentsCount} Active/Admitted students to prepare for the new semester?`)) {
                  setLoading(true);
                  const result = await studentService.deactivateStudents();
                  if (result.success) {
                    alert(`Successfully deactivated ${result.count} students. They are now 'Inactive' and will require semester activation.`);
                    // Refresh program data to update stats
                    fetchPrograms();
                  } else {
                    alert(`Failed to deactivate students: ${result.error}`);
                  }
                  setLoading(false);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed during active year and semester check:", error);
      }
    };
    fetchActiveYearAndCheckSemester();
  }, []);

  const quickActions = [
    {
      id: 1,
      title: 'New Program',
      description: 'Create academic program',
      icon: 'Plus',
      action: 'create-program',
      color: 'bg-blue-500',
      textColor: 'text-white'
    },
    {
      id: 2,
      title: 'Import Data',
      description: 'Bulk import records',
      icon: 'Upload',
      action: 'import-data',
      color: 'bg-green-500',
      textColor: 'text-white'
    },
    {
      id: 3,
      title: 'Generate Report',
      description: 'Create analytics report',
      icon: 'FileText',
      action: 'generate-reports',
      color: 'bg-purple-500',
      textColor: 'text-white'
    },
    {
      id: 4,
      title: 'Academic Year',
      description: 'Configure settings',
      icon: 'Calendar',
      action: 'manage-year',
      color: 'bg-orange-500',
      textColor: 'text-white'
    },
    {
      id: 5,
      title: 'Assessment',
      description: 'Setup framework',
      icon: 'ClipboardCheck',
      action: 'assessment',
      color: 'bg-red-500',
      textColor: 'text-white'
    },
    {
      id: 6,
      title: 'Timetable',
      description: 'Manage schedules',
      icon: 'Clock',
      action: 'timetable',
      color: 'bg-indigo-500',
      textColor: 'text-white'
    }
  ];

  const tabs = [
    { id: 'programs', label: 'Programs', icon: 'GraduationCap', description: 'Manage academic programs' },
    { id: 'units', label: 'Units', icon: 'BookOpen', description: 'Unit configuration' },
    { id: 'rooms', label: 'Rooms', icon: 'Box', description: 'Room management' },
    { id: 'enrollment', label: 'Enrollment', icon: 'Users', description: 'Student registration' },
    { id: 'statistics', label: 'Analytics', icon: 'BarChart3', description: 'Performance insights' }
  ];
  const displayedTabs = embedded
    ? (['programs', 'units', 'rooms', 'enrollment', 'statistics'].includes(initialTab) ? tabs.filter(tab => ['programs', 'units', 'rooms', 'enrollment', 'statistics'].includes(tab.id)) : tabs.filter(tab => tab.id === initialTab))
    : tabs;

  // Calculate statistics
  const totalPrograms = programs?.length || 0;
  const activePrograms = programs?.filter(p => p?.status === 'Active')?.length || 0;
  const totalEnrolled = programs?.reduce((sum, p) => sum + (p?.enrolledStudents || 0), 0);
  const totalCapacity = programs?.reduce((sum, p) => sum + (p?.capacity || 0), 0);
  const utilizationRate = totalCapacity > 0 ? Math.round((totalEnrolled / totalCapacity) * 100) : 0;

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Preserve the selected inner workspace in the URL. The staff shell keeps
    // `/role-based-dashboard/programs` as its route, while this query value
    // prevents a tab change from being lost on refresh or browser navigation.
    const nextSearch = tabId === 'programs' ? '' : `?tab=${encodeURIComponent(tabId)}`;
    navigate(`${location.pathname}${nextSearch}`, { replace: true });
    if (tabId !== 'units') {
      setSelectedProgramForUnits(null);
      setSelectedUnitType('all');
    }
  };

  const handleProgramEdit = (program) => {
    setShowDetailsModal(false);
    setSelectedProgram(program);
    setShowProgramModal(true);
  };

  const handleProgramDetails = (program) => {
    setSelectedProgram(program);
    setShowDetailsModal(true);
  };

  const handleManageUnits = (program, type = 'all') => {
    setSelectedProgramForUnits(program);
    setSelectedUnitType(type);
    setActiveTab('units');
  };

  const handleDeleteProgram = async (program) => {
    if (!program || !program.id) return;

    // Add confirmation if desired, but for now implementing the action
    const confirmed = window.confirm(`Are you sure you want to delete ${program.name}?`);
    if (!confirmed) return;

    try {
      setLoading(true);
      await programService.delete(program.id);
      await fetchPrograms(); // Refresh list
    } catch (err) {
      console.error("Failed to delete program:", err);
      setError("Failed to delete program");
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    setShowQuickActions(false);

    switch (action) {
      case 'create-program':
        setSelectedProgram(null);
        setShowProgramModal(true);
        break;
      case 'import-data': console.log('Import data functionality');
        break;
      case 'generate-reports':
        handleExportReport();
        break;
      case 'manage-year':
        navigate('/system-settings', { state: { section: 'calendar' } });
        break;
      case 'assessment': console.log('Assessment framework setup');
        break;
      case 'timetable': console.log('Timetable management');
        break;
      default:
        console.log('Quick action:', action);
    }
  };

  const handleSaveProgram = async (programData) => {
    try {
      // Transform UI data to database format
      const dbData = {
        code: programData?.code,
        name: programData?.name,
        departmentId: programData?.departmentId,
        isActive: programData?.status === 'Active',
        levels: programData?.levels || [],
        levelDurations: programData?.levelDurations || {},
        levelCapacities: programData?.levelCapacities || {},
        capacity: programData?.capacity
      };

      if (selectedProgram?.id) {
        // Update existing program
        await programService?.update(selectedProgram?.id, dbData);
      } else {
        // Create new program
        await programService?.create(dbData);
      }

      // Refresh program list
      await fetchPrograms();
      setShowProgramModal(false);
      setSelectedProgram(null);
    } catch (err) {
      console.error('Error saving program:', err);
      throw err; // Let modal handle error display
    }
  };

  const handleExportReport = () => {
    console.log('Exporting report...');
    // Implementation: Generate and download report
  };

  // Filter programs
  const filteredPrograms = programs?.filter(program => {
    const matchesSearch = program?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      program?.code?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    // Filter by level - check if program has this level in its levels array
    const matchesLevel = filterLevel === 'all' || program?.levels?.some(level => {
      const levelMap = {
        'Level 6 (Diploma)': 'level_6_diploma',
        'Level 5 (Craft)': 'level_5_craft',
        'Level 4 (Artisan)': 'level_4_artisan',
        'Level 3 (NVC)': 'level_3'
      };
      return level === levelMap?.[filterLevel];
    });

    // Filter by department
    const matchesDepartment = filterDepartment === 'all' ||
      program?.departmentId === filterDepartment ||
      program?.department === filterDepartment;

    return matchesSearch && matchesLevel && matchesDepartment;
  });

  const uniqueDepartments = [...new Set(programs.map(p => p.departmentId ? { id: p.departmentId, name: p.department } : null).filter(Boolean).map(JSON.stringify))].map(JSON.parse);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'programs':
        return (
          <div className="space-y-6">

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-12">

                <Icon name="Loader" size={32} className="animate-spin text-primary" />
                <span className="ml-3 text-muted-foreground">Loading programs...</span>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-error/10 border border-error rounded-lg p-4 text-center">
                <Icon name="AlertCircle" size={24} className="mx-auto text-error mb-2" />
                <p className="text-error">{error}</p>
                <Button variant="outline" size="sm" onClick={fetchPrograms} className="mt-3">
                  <Icon name="RefreshCw" size={14} className="mr-1" />
                  Retry
                </Button>
              </div>
            )}

            {/* Numbered programme list */}
            {!loading && !error && (
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                  <div className="hidden lg:grid lg:grid-cols-[2.5rem_minmax(12rem,2fr)_minmax(9rem,1.25fr)_6rem_5rem_minmax(16rem,auto)] gap-4 px-4 py-3 border-b bg-muted/50 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><span>No.</span><span>Programme</span><span>Levels</span><span>Enrolment</span><span>Units</span><span className="text-right">Actions</span></div>
                  <div className="divide-y divide-border">
                {filteredPrograms?.map((program,index) => (
                  <ProgramCard
                    key={program?.id}
                    serial={index+1}
                    program={program}
                    onEdit={handleProgramEdit}
                    onViewDetails={handleProgramDetails}
                    onManageUnits={handleManageUnits}
                    onDelete={handleDeleteProgram}
                  />
                ))}
                  </div>
              </div>
            )}

            {!loading && !error && filteredPrograms?.length === 0 && (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No programs found</h3>
                <p className="text-muted-foreground">Try adjusting your search or filters</p>
              </div>
            )}
          </div>
        );

      case 'enrollment':
        return <EnrollmentManagement />;

      case 'units':
        return (
          <div className="space-y-6 pt-6">
            <UnitManagement
              trainerOnly={myUnitsOnly}
              program={selectedProgramForUnits}
              initialType={selectedUnitType}
              onBack={() => {
                setSelectedProgramForUnits(null);
                setSelectedUnitType('all');
                setActiveTab('programs');
              }}
            />
          </div>
        );

      case 'rooms':
        return (
          <div className="space-y-6 pt-6">
            <RoomManagement />
          </div>
        );

      case 'statistics':
        return (
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-foreground">Analytics & Reports</h2>
                  <p className="text-sm text-muted-foreground">
                    View enrollment statistics and performance insights
                  </p>
                </div>
                <Button variant="default" size="sm" onClick={handleExportReport}>
                  <Icon name="Download" size={14} className="mr-1" />
                  Export Report
                </Button>
              </div>
            </div>
            <StatisticsPanel programs={programs} />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout title="Academic Management">
      <div className="min-h-screen bg-background">
        {/* Single-line Minimalistic Header Bar */}
        <div className="bg-card border-b border-border sticky top-0 z-40 shadow-sm p-2.5">
          <div className="mx-auto flex max-w-7xl flex-col items-stretch gap-3 lg:flex-row lg:items-center lg:justify-between">
            {/* Title & Navigation Tabs */}
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {onBack && (
                <>
                  <button
                    type="button"
                    onClick={onBack}
                    className="whitespace-nowrap text-xs font-semibold text-blue-700 hover:underline"
                  >
                    ← Staff dashboard
                  </button>
                  <span className="text-xs text-slate-300">/</span>
                </>
              )}
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-primary/10 border border-primary/20 text-primary">
                <Icon name="GraduationCap" size={18} />
              </div>
              <div className="flex min-w-0 flex-wrap items-baseline gap-2">
                <h1 className="text-base font-bold tracking-tight text-foreground">Academic Management</h1>
                <span className="text-xs text-muted-foreground font-medium">· {tabs.find(tab => tab.id === activeTab)?.label}</span>
              </div>

              {/* Navigation Tabs */}
              <nav className="flex w-full flex-wrap items-center gap-1 pt-1 lg:ml-2 lg:w-auto lg:pt-0">
                {displayedTabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => handleTabChange(tab?.id)}
                    className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                      activeTab === tab?.id
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={13} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Right: Search, Filters & Action Button for Programs Tab */}
            {activeTab === 'programs' && (
              <div className="flex w-full max-w-none flex-wrap items-center gap-2 lg:flex-1 lg:justify-end lg:max-w-2xl">
                <div className="relative min-w-0 flex-1 lg:max-w-[220px]">
                  <Icon name="Search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search programs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="w-full pl-8 pr-3 py-1 text-xs border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <select
                  value={filterLevel}
                  onChange={(e) => setFilterLevel(e?.target?.value)}
                  className="w-full px-2 py-1 text-xs border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary sm:w-auto"
                >
                  <option value="all">All Levels</option>
                  <option value="Level 6 (Diploma)">Level 6</option>
                  <option value="Level 5 (Craft)">Level 5</option>
                  <option value="Level 4 (Artisan)">Level 4</option>
                  <option value="Level 3 (NVC)">Level 3</option>
                </select>

                <select
                  value={filterDepartment}
                  onChange={(e) => setFilterDepartment(e?.target?.value)}
                  className="w-full px-2 py-1 text-xs border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary sm:w-auto sm:max-w-[150px]"
                >
                  <option value="all">All Depts</option>
                  {uniqueDepartments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>

                {canCreateProgram && (
                  <Button
                    variant="default"
                    size="sm"
                    className="h-7 px-2.5 text-xs font-semibold shrink-0"
                    onClick={() => {
                      setSelectedProgram(null);
                      setShowProgramModal(true);
                    }}
                  >
                    <Icon name="Plus" size={13} className="mr-1" />
                    Add Program
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0">
          {renderTabContent()}
        </div>

        {/* Click outside to close quick actions */}
        {showQuickActions && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowQuickActions(false)}
          />
        )}

        {/* Modals */}
        <ProgramModal
          isOpen={showProgramModal}
          onClose={() => {
            setShowProgramModal(false);
            setSelectedProgram(null);
          }}
          program={selectedProgram}
          onSave={handleSaveProgram}
        />

        <ProgramDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedProgram(null);
          }}
          program={selectedProgram}
          onEdit={handleProgramEdit}
        />

      </div>
    </Layout>
  );
};

export default AcademicManagement;
